import { useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import {
  Button,
  DateRangeChip,
  Drawer,
  FilterBar,
  FilterChip,
  Money,
  MultiSelectChip,
  SelectChip,
  Segmented,
} from '../components'
import { useAppState } from '../app-state'
import { ApiError } from '../lib/api'
import { useCashflow, type CashflowFilters } from '../lib/api/reports'
import { useAccounts } from '../lib/api/accounts'
import { useOrganizations } from '../lib/api/organizations'
import { useProjects } from '../lib/api/projects'
import { useTags } from '../lib/api/tags'
import { flattenTransactions, useTransactions } from '../lib/api/transactions'
import { T } from '../data/mock'
import type { CashflowGroup, CashflowResponse } from '../lib/api/types'
import {
  CashflowAmount,
  defaultRange,
  periodToDateRange,
  type CurrencyMode,
  type DrillCell,
  type GroupBy,
  type PeriodGrain,
} from './cashflow-helpers'
import { CashflowSection } from './cashflow-section'

/** Bold pivot row summing one band across all periods (total income / expense / net). */
function CashflowTotalRow({
  label,
  totals,
  periods,
  currencyMode,
  sticky = false,
}: {
  label: string
  totals: Record<string, number | Record<string, number>>
  periods: string[]
  currencyMode: CurrencyMode
  sticky?: boolean
}) {
  return (
    <tr className="subtotal" style={sticky ? { position: 'sticky', bottom: 0 } : undefined}>
      <td>
        <span style={{ fontWeight: 600, color: 'var(--fg-1)' }}>{label}</span>
      </td>
      {periods.map((p) => (
        <td key={p} className="num">
          <CashflowAmount v={totals[p]} bold />
        </td>
      ))}
      {currencyMode === 'base' && (
        <td className="num">
          <CashflowAmount
            v={periods.reduce((sum, p) => sum + ((totals[p] as number) ?? 0), 0)}
            bold
          />
        </td>
      )}
    </tr>
  )
}

type Props = { railSelected: string }

export function Cashflow({ railSelected }: Props) {
  const navigate = useNavigate()
  const { setDrawerTx } = useAppState()
  const range = useMemo(defaultRange, [])
  const [dateFrom, setDateFrom] = useState(range.from)
  const [dateTo, setDateTo] = useState(range.to)
  const [groupBy, setGroupBy] = useState<GroupBy>('category')
  const [period, setPeriod] = useState<PeriodGrain>('month')
  const [currencyMode, setCurrencyMode] = useState<CurrencyMode>('base')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [drillCell, setDrillCell] = useState<DrillCell | null>(null)
  const [organizationId, setOrganizationId] = useState<string | null>(null)
  const [projectIds, setProjectIds] = useState<string[]>([])
  const [tagIds, setTagIds] = useState<string[]>([])

  const accounts = useAccounts()
  const organizations = useOrganizations()
  const projects = useProjects({ organizationId })
  const tags = useTags()

  const railAccount =
    railSelected && railSelected !== 'all'
      ? accounts.data?.find((a) => a.id === railSelected) ?? null
      : null

  const filters: CashflowFilters = {
    dateFrom,
    dateTo,
    groupBy,
    period,
    currencyMode,
    accountIds: railAccount ? [railAccount.id] : undefined,
    organizationId: organizationId ?? (railAccount?.organizationId ?? null),
    projectIds: projectIds.length ? projectIds : undefined,
    tagIds: tagIds.length ? tagIds : undefined,
  }

  const cashflow = useCashflow(filters)
  const data = cashflow.data as CashflowResponse | undefined

  const drillRange = drillCell ? periodToDateRange(drillCell.periodKey, period) : null
  const drillTxs = useTransactions(
    drillCell && drillRange
      ? {
          categoryId: groupBy === 'category' ? drillCell.group.id : undefined,
          projectId:
            groupBy === 'project' && drillCell.group.id !== '_unassigned'
              ? drillCell.group.id
              : undefined,
          accountId: railAccount?.id ?? null,
          dateFrom: drillRange.from,
          dateTo: drillRange.to,
          limit: 50,
        }
      : {},
    { enabled: !!(drillCell && drillRange) },
  )

  const toggleExpand = (id: string) => {
    const next = new Set(expanded)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setExpanded(next)
  }

  const buildVisibleRows = (groups: CashflowGroup[]): CashflowGroup[] => {
    if (groupBy !== 'category') return groups
    const byId = new Map(groups.map((g) => [g.id, g]))
    const rootIds = groups.filter((g) => !g.parentId || !byId.has(g.parentId)).map((g) => g.id)
    const out: CashflowGroup[] = []
    const walk = (id: string) => {
      const g = byId.get(id)
      if (!g) return
      out.push(g)
      if (expanded.has(id)) {
        for (const child of groups) {
          if (child.parentId === id) walk(child.id)
        }
      }
    }
    for (const id of rootIds) walk(id)
    return out
  }

  const groups = data?.groups ?? []
  const incomeGroups = groups.filter((g) => g.kind === 'income')
  const expenseGroups = groups.filter((g) => g.kind === 'expense')
  const visibleIncome = buildVisibleRows(incomeGroups)
  const visibleExpense = buildVisibleRows(expenseGroups)
  const hasChildren = (id: string) => groups.some((g) => g.parentId === id)
  const periods = data?.periods ?? []
  const orgs = organizations.data ?? []
  const orgName = railAccount?.organizationId
    ? orgs.find((o) => o.id === railAccount.organizationId)?.name
    : null

  return (
    <>
      <FilterBar>
        <DateRangeChip
          from={dateFrom}
          to={dateTo}
          onChange={(f, t) => {
            setDateFrom(f)
            setDateTo(t)
          }}
        />
        {railAccount ? (
          <FilterChip label="Рахунок" value={railAccount.name} />
        ) : (
          organizations.data &&
          organizations.data.length > 1 && (
            <SelectChip
              label="Орг"
              items={organizations.data}
              selectedId={organizationId}
              onChange={setOrganizationId}
            />
          )
        )}
        {orgName && railAccount && <FilterChip label="Орг" value={orgName} />}
        <MultiSelectChip
          label="Проєкти"
          items={projects.data ?? []}
          selectedIds={projectIds}
          onChange={setProjectIds}
        />
        <MultiSelectChip
          label="Теги"
          items={tags.data ?? []}
          selectedIds={tagIds}
          onChange={setTagIds}
        />
        <Segmented<PeriodGrain>
          options={[
            { value: 'month', label: 'Місяць' },
            { value: 'quarter', label: 'Квартал' },
            { value: 'year', label: 'Рік' },
          ]}
          value={period}
          onChange={setPeriod}
        />
        <Segmented<GroupBy>
          options={[
            { value: 'category', label: 'Категорії' },
            { value: 'project', label: 'Проєкти' },
          ]}
          value={groupBy}
          onChange={setGroupBy}
        />
        <Segmented<CurrencyMode>
          options={[
            { value: 'base', label: 'База UAH' },
            { value: 'native', label: 'Нативна' },
          ]}
          value={currencyMode}
          onChange={setCurrencyMode}
        />
      </FilterBar>

      <div className="content">
        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto', maxHeight: 'calc(100vh - 220px)' }}>
            {cashflow.isLoading ? (
              <div style={{ padding: 24, color: 'var(--fg-3)' }}>Завантаження…</div>
            ) : cashflow.isError ? (
              <CashflowError
                error={cashflow.error}
                period={period}
                onCoarsen={() => setPeriod(period === 'month' ? 'quarter' : 'year')}
              />
            ) : periods.length === 0 ? (
              <div style={{ padding: 24, color: 'var(--fg-3)' }}>Немає даних у діапазоні</div>
            ) : (
              <table className="tbl pivot">
                <thead>
                  <tr>
                    <th>{groupBy === 'category' ? 'Категорія' : 'Проєкт'}</th>
                    {periods.map((p) => (
                      <th key={p} className="num" style={{ width: 120 }}>
                        {p}
                      </th>
                    ))}
                    {currencyMode === 'base' && (
                      <th className="num" style={{ width: 130 }}>
                        Усього
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  <CashflowSection
                    title="Надходження"
                    items={visibleIncome}
                    periods={periods}
                    expanded={expanded}
                    groupBy={groupBy}
                    currencyMode={currencyMode}
                    onToggle={toggleExpand}
                    onDrill={setDrillCell}
                    hasChildren={hasChildren}
                  />
                  {data && incomeGroups.length > 0 && (
                    <CashflowTotalRow
                      label="Усього надходжень"
                      totals={data.totals.income}
                      periods={periods}
                      currencyMode={currencyMode}
                    />
                  )}
                  <CashflowSection
                    title="Витрати"
                    items={visibleExpense}
                    periods={periods}
                    expanded={expanded}
                    groupBy={groupBy}
                    currencyMode={currencyMode}
                    onToggle={toggleExpand}
                    onDrill={setDrillCell}
                    hasChildren={hasChildren}
                  />
                  {data && expenseGroups.length > 0 && (
                    <CashflowTotalRow
                      label="Усього витрат"
                      totals={data.totals.expense}
                      periods={periods}
                      currencyMode={currencyMode}
                    />
                  )}
                  {data && (
                    <CashflowTotalRow
                      label="Чистий cash-flow"
                      totals={data.totals.net}
                      periods={periods}
                      currencyMode={currencyMode}
                      sticky
                    />
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {drillCell && (
        <Drawer
          title={`${drillCell.group.name} · ${drillCell.periodKey}`}
          onClose={() => setDrillCell(null)}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 500,
              color: 'var(--fg-3)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: 8,
            }}
          >
            Операції
          </div>
          {drillTxs.isLoading ? (
            <div style={{ color: 'var(--fg-3)' }}>Завантаження…</div>
          ) : (
            <table className="tbl" style={{ fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ width: 80 }}>{T.table.date}</th>
                  <th>{T.table.counterparty}</th>
                  <th className="num" style={{ width: 110 }}>
                    {T.table.amount}
                  </th>
                </tr>
              </thead>
              <tbody>
                {flattenTransactions(drillTxs.data).map((tx) => (
                  <tr
                    key={tx.id}
                    onClick={() => setDrawerTx({ mode: 'edit', transaction: tx })}
                    style={{ cursor: 'pointer' }}
                    title="Відкрити операцію"
                  >
                    <td className="fg3">{tx.date}</td>
                    <td>{tx.description ?? '—'}</td>
                    <td className="num">
                      <Money
                        amount={tx.type === 'expense' || tx.type === 'transfer' ? -tx.amount : tx.amount}
                        currency={tx.currency}
                        sign
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div style={{ marginTop: 16 }}>
            <Button
              variant="ghost"
              size="sm"
              icon="arrow-right"
              onClick={() => {
                setDrillCell(null)
                navigate({ to: '/transactions' })
              }}
            >
              Відкрити в /transactions
            </Button>
          </div>
        </Drawer>
      )}
    </>
  )
}

// Translates the worker's structured error codes into a hint that names a
// fix the user can act on. `range_too_wide` is the realistic case ("Весь
// період" with monthly grain blows past the 60-period cap); offering to
// coarsen the grain is faster than asking the user to shrink the dates.
function CashflowError({
  error,
  period,
  onCoarsen,
}: {
  error: unknown
  period: PeriodGrain
  onCoarsen: () => void
}) {
  let code: string | null = null
  if (error instanceof ApiError) {
    try {
      const parsed = JSON.parse(error.body) as { error?: string }
      code = parsed.error ?? null
    } catch {
      /* fall through */
    }
  }
  if (code === 'range_too_wide') {
    const next = period === 'month' ? 'Квартал' : period === 'quarter' ? 'Рік' : null
    return (
      <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ color: 'var(--warning-fg)' }}>
          Діапазон занадто широкий для деталізації «
          {period === 'month' ? 'Місяць' : period === 'quarter' ? 'Квартал' : 'Рік'}
          ».
        </div>
        <div style={{ color: 'var(--fg-3)', fontSize: 13 }}>
          Виберіть коротший період або переключіться на більшу деталізацію.
        </div>
        {next && (
          <div>
            <Button variant="secondary" size="sm" onClick={onCoarsen}>
              Переключити на «{next}»
            </Button>
          </div>
        )}
      </div>
    )
  }
  return (
    <div style={{ padding: 24, color: 'var(--danger-fg)' }}>
      Помилка завантаження звіту
      {code ? `: ${code}` : ''}
    </div>
  )
}

